<template>
  <VDataGridV2
    class="order-options-table"
    :data="orderOptions"
    sticky
    layout="fill"
  >
    <VDataGridColumnV2
      id="manufacturer"
      width="20%"
    >
      <template #label>
        Производитель
        <div class="description">
          Юр. лицо поставщика
        </div>
      </template>
      <template #default="{ row }">
        <!-- Одна претензия на связку поставщик + юр. лицо + производитель — вешается
             и на производителя, и на юр. лицо: какое из полей менять, решает пользователь -->
        <div
          class="title"
          :data-mismatch="row.getBundleMismatch(instanceOfAbstractDemand.product)"
        >
          {{ row.manufacturer.name }}
        </div>

        <div
          class="title description"
          :data-mismatch="row.getBundleMismatch(instanceOfAbstractDemand.product)"
        >
          Юр. лицо: {{ row.sellerCompany.name }}
        </div>

        <div class="description">
          Поставщик: {{ row.seller.name }}
        </div>

        <div
          class="description"
          :data-mismatch="instanceOfAbstractDemand.product.hasExpirationRequirement && row.getExpirationDateMismatch(instanceOfAbstractDemand.product.requiredExpirationDate)"
        >
          <span class="nowrap">
            Годен до: {{ $core.utils.date.humanizeDate(row.productExpirationDate, null) }}
          </span>
        </div>

        <VBadges v-if="row.isAcceptingPreorder">
          <VBadge>
            Доступен предзаказ от {{ row.preorderMinQty?.format(row.unit) }}
          </VBadge>
          <VBadge>
            Страховой запас:
            <template v-if="row.preorderMonthlyReserve?.isEmpty() !== false">нет</template>
            <template v-else>{{ row.preorderMonthlyReserve.format(row.unit) }}</template>
          </VBadge>
        </VBadges>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="characteristics"
      label="Характеристики"
      class="characteristics"
    >
      <template #default="{ row }">
        <div class="formal-name">
          Наименование сырья: {{ row.name }}
        </div>
        <div
          v-if="$core.utils.isNotEmptyArray(row.getCharacteristics())"
          class="characteristics"
        >
          <div
            v-for="(charac, index) in row.getCharacteristics()"
            :key="row.id + '_' + charac.characteristic.id + '_' + index"
            class="characteristics-item title"
            :class="{ 'up': index >= 5 }"
            :data-mismatch="charac.getMismatchByProduct(instanceOfAbstractDemand.product)"
          >
            {{ charac.name }}: {{ charac.valueStringRepresentation }}
          </div>
        </div>

        <div class="footnote">
          Описание: {{ row.description || '--' }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="receiptPeriod"
      label="Интервал получения"
      width="12%"
    >
      <template #default="{ row }">
        <div
          class="title"
          :data-mismatch="getReceiptMismatch(row)"
        >
          <span class="nowrap">
            {{ getReceiptPeriod(row).toString() }}
          </span>
        </div>
        <div class="description">
          Доставляется {{ row.deliveryType.name.toLowerCase() }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="qty"
      label="Доступно"
      width="10%"
      content-align="right"
    >
      <template #default="{ row }">
        <div class="title">
          <span class="nowrap">
            {{ row.qtyLeft.format(row.unit) }}
          </span>
        </div>
        <div class="description">
          Упаковка: {{ row.package?.name }}
        </div>
        <div class="description">
          Фасовка: {{ row.packageVolume?.format(row.unit) }}
        </div>
        <VBadges>
          <VBadge
            v-if="TenderBid.isTenderBid(row) && row.qtyOrdered.isPositive() && tender?.demand"
            class="nowrap primary"
          >
            Заказано
            {{ row.qtyOrdered.format(row.unit) }}
            ({{
              $core.utils.number.getPercentageOf(row.qtyOrdered.toNumber(), tender.demand.qtyDesired.toNumber())
            }} % )
          </VBadge>
        </VBadges>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="price"
      width="18%"
      content-align="right"
    >
      <template #label>
        Цена покупателя с отсрочкой
        <div class="description">
          Тип доставки
        </div>
      </template>
      <!-- класс link (попап с ценой поставщика) — только для ЛСЗ / Намангана -->
      <template #default="{ row }">
        <!-- цена с отсрочкой — у ставки тендера -->
        <!-- попап навешен на обёртку, а не на цену: иначе он накрывает подстрочник -->
        <div
          v-if="row.priceFullBuyerWithDeferredPayment?.isEmpty() === false"
          class="price-block"
          @mouseenter="$core.tooltip.show($event.currentTarget, priceTooltip(row))"
          @mouseleave="$core.tooltip.hide()"
        >
          <div
            class="price nowrap up right"
            :class="{ 'btn-link': $core.currentPartner?.isLSZ || $core.currentPartner?.isNamangan }"
            :data-mismatch="row.getPriceWithDeferredPaymentMismatch()"
          >
            {{
              row.priceFullBuyerWithDeferredPayment.formatInDisplayCurrency(row.offerCurrencyCode, row.getDateForCurrencyRate(), row.unit)
            }}
          </div>
          <!-- цена показана в валюте платформы — показываем, из чего пересчитана -->
          <div
            v-if="$core.displayedCurrencyCode !== row.offerCurrencyCode"
            class="description"
          >
            Пересчитано из {{ row.priceFullBuyerWithDeferredPayment.format(row.offerCurrencyCode, row.unit) }}
          </div>
        </div>
        <!-- обычная цена покупателя — у Offer -->
        <div
          v-else-if="row.priceFullBuyer?.isEmpty() === false"
          class="price-block"
          @mouseenter="$core.tooltip.show($event.currentTarget, priceTooltip(row))"
          @mouseleave="$core.tooltip.hide()"
        >
          <div
            class="price nowrap up right"
            :class="{ 'btn-link': $core.currentPartner?.isLSZ || $core.currentPartner?.isNamangan }"
          >
            {{
              row.priceFullBuyer.formatInDisplayCurrency(row.offerCurrencyCode, row.getDateForCurrencyRate(), row.unit)
            }}
          </div>
          <!-- цена показана в валюте платформы — показываем, из чего пересчитана -->
          <div
            v-if="$core.displayedCurrencyCode !== row.offerCurrencyCode"
            class="description"
          >
            Пересчитано из {{ row.priceFullBuyer.format(row.offerCurrencyCode, row.unit) }}
          </div>
        </div>
        <template v-else>--</template>
        <div class="description">
          {{ row.deliveryType.name }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="$core.currentPartner?.isBuyer"
      id="action"
      width="4%"
      content-align="right"
    >
      <template #default="{ row }">
        <VButton
          :disabled="TenderBid.isTenderBid(row) && !row.tender?.isFinished"
          @click="openBuyDialog(row)"
        >
          <template #icon>
            <VIcon material="add_shopping_cart"/>
          </template>
        </VButton>
      </template>
    </VDataGridColumnV2>
  </VDataGridV2>
</template>

<script lang="ts">
import type Demand from '@/domains/Demand/Demand';
import type DateRange from '@/core/types/DateRange';
import type AbstractOrderOption from '@/domains/Abstract/AbstractOrderOption';
import Offer from '@/domains/Offers/Offer';
import Tender from '@/domains/Tenders/Tender';
import TenderBid from '@/domains/TenderBids/TenderBid';

/**
 * Предложения поставщиков либо ставки тендера — что именно, решает
 * вызывающий: обе сущности наследуют AbstractOrderOption и отвечают на
 * одни и те же методы сравнения.
 *
 * Эталон приходит одним объектом: тендер и потребность оба наследуют
 * AbstractDemand и несут всё, с чем сравниваем.
 *
 * Расхождения подсвечиваются атрибутом data-mismatch — точку и подсказку
 * рисует CSS диалога. Покупка оформляется здесь же, наружу уходит только
 * факт сохранения.
 */
export default defineComponent({
  name: 'OrderOptionsTable',

  props: {
    orderOptions: {
      type: Array as PropType<AbstractOrderOption[]>,
      required: true,
    },

    /** С чем сравниваем: тендер или потребность. */
    instanceOfAbstractDemand: {
      type: Object as PropType<Tender | Demand>,
      required: true,
    },
  },

  emits: ['save'],

  setup() {
    return {
      /** Шаблону нужен type guard: ставка и предложение живут в одной
       *  таблице, но ведут себя по-разному. */
      TenderBid,
    };
  },

  computed: {
    /** Тендер, если сравниваем с ним. Потребность тендером не является. */
    tender(): Tender | null {
      if (this.instanceOfAbstractDemand instanceof Tender) {
        return this.instanceOfAbstractDemand;
      } else {
        return null;
      }
    },

    /** Потребность, если сравниваем с ней. */
    demand(): Demand | null {
      if (this.instanceOfAbstractDemand instanceof Tender) {
        return null;
      } else {
        return this.instanceOfAbstractDemand as Demand;
      }
    },
  },

  methods: {
    /**
     * Что показываем в колонке периода: предзаказу важен период отгрузки,
     * обычной потребности — период получения.
     */
    getReceiptPeriod(row: AbstractOrderOption): DateRange {
      if (this.instanceOfAbstractDemand.isPreorderNeeded) {
        return row.shipmentPeriod;
      } else {
        return row.receiptPeriod;
      }
    },

    /** Расхождение по тому же периоду — сравнивается с тем же требованием. */
    getReceiptMismatch(row: AbstractOrderOption): string | null {
      if (this.instanceOfAbstractDemand.isPreorderNeeded) {
        return row.getPreorderShipmentPeriodMismatch(this.instanceOfAbstractDemand.preorderShipmentPeriod!);
      } else {
        return row.getReceiptDateMismatch(this.instanceOfAbstractDemand.desiredReceiptDate);
      }
    },

    /**
     * Тултип к цене покупателя: полная сумма покупателя и цена поставщика.
     * Обе строки выводятся всегда, при отсутствии значения — прочерк.
     */
    priceTooltip(row: any): string {
      const lines = [];

      /** sumFullBuyer есть только у Offer — у ставки тендера суммы нет, ставим прочерк */
      if (this.$core.utils.isNullOrUndefined(row.sumFullBuyer) === false) {
        lines.push(`Полная сумма покупателя: ${row.sumFullBuyer.formatInDisplayCurrency(row.offerCurrencyCode, row.getDateForCurrencyRate())}`);
      } else {
        lines.push('Полная сумма покупателя: --');
      }

      /** в попапе показываем цену поставщика — в ячейке выведена цена покупателя */
      if (this.$core.utils.isNullOrUndefined(row.priceFullSeller) === false) {
        lines.push(`Цена поставщика: ${row.priceFullSeller.formatInDisplayCurrency(row.offerCurrencyCode, row.getDateForCurrencyRate(), row.unit)}`);
      } else {
        lines.push('Цена поставщика: --');
      }

      return lines.join('\n');
    },

    /** Оформление заказа по выбранному предложению или ставке. */
    openBuyDialog(row: AbstractOrderOption): void {
      const props: Record<string, any> = {
        showOrderType: true,
      };

      if (this.demand) {
        props.demand = this.demand;
      }

      if (TenderBid.isTenderBid(row)) {
        props.tender = this.tender;
        row.tender = this.tender!;
        props.tenderBid = row;
      }

      if (Offer.isOffer(row)) {
        props.offer = row;
      }

      this.$core.dialogs.OrderCreateDialogMinimized.show(
        props,
        {
          save: () => {
            this.$emit('save');
          },
        },
      );
    },
  },
});
</script>